import { DefaultProjectorFn, MemoizedSelector, createSelector } from '@ngrx/store';

import {
  DashboardAnomaly,
  DashboardDevice,
  DashboardEditColumnPayload,
  DashboardFilterPayload,
  DashboardInstallationData,
  DashboardColumnDataWithoutInstallationDetails,
  ButtonFilterDataName,
  DashboardFilterName,
  DashboardFilterValues,
} from '@vi-iot/client';

import { AppState } from '../app.state';
import { ACTIVE_ERROR_TYPE } from '../active-errors';

import { GenericStoreStatus } from '../installation-details';
import {
  ColumnCreationFlow,
  DashboardInstallationColumn,
  InstallationListDashboardState,
  KanbanBoardState,
  KanbanColumnPrimaryData,
} from './kanban-board.state';
import {
  DashboardFilter,
  DashboardListFilterButtonData,
  QuickViewErrorDetails,
  QuickViewSmartNotes,
  limitOfSelfDefinedColumns,
} from './kanban-board.model';
import { KanbanBoardMappers } from './kanban-board.mappers';

export namespace KanbanBoardSelectors {
  const kanbanBoard = (state: AppState): KanbanBoardState => state.kanbanBoard;

  export const selectColumns = createSelector(
    kanbanBoard,
    (state: KanbanBoardState): DashboardInstallationColumn[] => state.columns
  );

  export const selectColumnDialogVisibility = createSelector(
    kanbanBoard,
    (state: KanbanBoardState): boolean => state.columnDialogVisible
  );

  export const selectIsTempColumn = createSelector(selectColumns, (columns): boolean => {
    return !!columns.some((column) => column.isTemporary);
  });

  export const selectLastColumnId = createSelector(selectColumns, (columns): number => {
    const filteredColumns = columns.filter((column) => !column.isTemporary);
    const lastColumn = filteredColumns[filteredColumns.length - 1];

    return lastColumn.id;
  });

  export const selectFetchingDashboardStatus = createSelector(
    kanbanBoard,
    (state: KanbanBoardState): GenericStoreStatus => state.fetchingDashboardStatus
  );

  export const selectColumnDialogData = createSelector(
    kanbanBoard,
    (state: KanbanBoardState): DashboardInstallationColumn => state.columnDialogData
  );

  export const selectFilters = createSelector(
    kanbanBoard,
    (state: KanbanBoardState): DashboardFilter[] => {
      return state.filters;
    }
  );

  export const selectPredefinedFilters = createSelector(
    kanbanBoard,
    (state: KanbanBoardState): DashboardFilter[] => {
      return state.predefinedFilters;
    }
  );

  export const selectSelectedFilters = createSelector(
    kanbanBoard,
    (state: KanbanBoardState): DashboardFilter[] => {
      return state.selectedFilters;
    }
  );

  export const selectSelectedAndMappedFilters = createSelector(
    kanbanBoard,
    (state: KanbanBoardState): DashboardFilterPayload => {
      return state.selectedFilters.length
        ? KanbanBoardMappers.extractFiltersPayload(state.selectedFilters)
        : {};
    }
  );

  export const selectIndexOfSelectedDeviceInQuickView = createSelector(
    kanbanBoard,
    (state: KanbanBoardState): number | null => state.quickViewDashboardData.quickViewDeviceIndex
  );

  export const selectQuickViewCurrentDevice = createSelector(
    kanbanBoard,
    (state: KanbanBoardState): DashboardDevice => {
      return state.quickViewDashboardData.installation?.devices
        ? state.quickViewDashboardData.installation.devices[
            state.quickViewDashboardData.quickViewDeviceIndex
          ]
        : null;
    }
  );

  const selectAllErrorSmartNotes = createSelector(
    kanbanBoard,
    (state: KanbanBoardState): QuickViewSmartNotes[] =>
      state.quickViewDashboardData.smartNotes?.filter(
        (smartNote) => smartNote.type === ACTIVE_ERROR_TYPE.DEVICE_ERROR
      ) || []
  );

  export const selectValidHintSmartNotes = createSelector(
    kanbanBoard,
    (state: KanbanBoardState): QuickViewSmartNotes[] =>
      state.quickViewDashboardData.smartNotes?.filter(
        (smartNote) =>
          smartNote.type !== ACTIVE_ERROR_TYPE.DEVICE_ERROR &&
          (Object.values(ACTIVE_ERROR_TYPE) as string[]).includes(smartNote.type)
      ) || []
  );

  export const selectErrorsForSpecificDevice = createSelector(
    selectQuickViewCurrentDevice,
    selectAllErrorSmartNotes,
    (device: DashboardDevice, errorSmartNotes: QuickViewSmartNotes[]): QuickViewErrorDetails[] => {
      const idsOfRequiredSmartNotes = device?.smartNotes.map((smartNote) => smartNote.id) || [];
      const matchingSmartNotes = errorSmartNotes.filter((smartNote) =>
        idsOfRequiredSmartNotes.includes(smartNote.id)
      );
      const mappedMatchingSmartNotes: QuickViewErrorDetails[] = matchingSmartNotes.map(
        (smartNote): QuickViewErrorDetails => ({
          appearedAt: smartNote?.content.appearedAt,
          cause: smartNote?.faultCodes?.reduce(
            (acc, faultCode) => [...acc, faultCode.causes.map((cause) => cause.cause).join(',')],
            []
          ),
          errorCode: smartNote.content.errorCode,
          errorDescription: smartNote.content?.errorDescription,
          loading: smartNote.loading,
          error: smartNote.error,
        })
      );

      return mappedMatchingSmartNotes;
    }
  );

  export const selectIsSmartNotesFetched = createSelector(
    kanbanBoard,
    (state: KanbanBoardState): boolean => !!state.quickViewDashboardData.smartNotes
  );

  export const selectQuickViewAnomalies = createSelector(
    selectQuickViewCurrentDevice,
    (device: DashboardDevice): DashboardAnomaly[] => device?.anomalies || []
  );

  export const selectQuickViewInstallation = createSelector(
    kanbanBoard,
    (state: KanbanBoardState): DashboardInstallationData =>
      state.quickViewDashboardData.installation
  );

  export const selectIsSelectedDeviceWithSystemLocked = createSelector(
    selectValidHintSmartNotes,
    (smartNotes: QuickViewSmartNotes[]): boolean =>
      smartNotes.some((smartNote) => smartNote.type === ACTIVE_ERROR_TYPE.SYSTEM_LOCKED)
  );

  export const selectIsSelectedDeviceWithFLoorHeatingOverheating = createSelector(
    selectValidHintSmartNotes,
    (smartNotes: QuickViewSmartNotes[]): boolean =>
      smartNotes.some((smartNote) => smartNote.type === ACTIVE_ERROR_TYPE.FLOOR_HEATING_OVERHEATING)
  );

  export const selectIsSystemWorkingCorrectly = createSelector(
    selectAllErrorSmartNotes,
    selectValidHintSmartNotes,
    selectQuickViewAnomalies,
    (
      errorSmartNotes: QuickViewSmartNotes[],
      hintSmartNotes: QuickViewSmartNotes[],
      anomalies: DashboardAnomaly[]
    ) => !errorSmartNotes.length && !hintSmartNotes.length && !anomalies.length
  );

  export const selectAllColumnNames = createSelector(
    kanbanBoard,
    (state: KanbanBoardState): string[] => state.columns.map((column) => column.name)
  );

  export const selectColumnVersion = (
    columnId: number
  ): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> => {
    return createSelector(
      kanbanBoard,
      (state: KanbanBoardState): string =>
        state.columns.find((column) => column.id === columnId).version
    );
  };

  export const selectForcedStoreUpdateStatus = createSelector(
    kanbanBoard,
    (state: KanbanBoardState): GenericStoreStatus => state.forcedStoreUpdateStatus
  );

  export const selectIsColumnNameInputVisible = createSelector(
    kanbanBoard,
    (state: KanbanBoardState): boolean => state.columns.some((column) => column.inputShowed)
  );

  export const selectColumnsAvailableForInstallation = (
    installationId: number
  ): MemoizedSelector<
    AppState,
    KanbanColumnPrimaryData[],
    DefaultProjectorFn<KanbanColumnPrimaryData[]>
  > => {
    return createSelector(kanbanBoard, (state: KanbanBoardState): KanbanColumnPrimaryData[] => {
      const output: KanbanColumnPrimaryData[] = [];

      state.columns.forEach((column, index) => {
        if (
          column.selfDefined &&
          !column.installations.some((installation) => installation.id === installationId)
        ) {
          const { id, version, name } = column;
          output.push({ id, version, name, index });
        }
      });

      return output;
    });
  };

  export const selectIsAnySystemStatusData = createSelector(
    selectIsSelectedDeviceWithSystemLocked,
    selectIsSelectedDeviceWithFLoorHeatingOverheating,
    selectIsSystemWorkingCorrectly,
    selectErrorsForSpecificDevice,
    (
      isSystemLocked: boolean,
      isFloorHeatingOverHeating: boolean,
      isSystemWorkingCorrectly: boolean,
      quickViewErrorDetails: QuickViewErrorDetails[]
    ): boolean =>
      isSystemLocked ||
      isFloorHeatingOverHeating ||
      isSystemWorkingCorrectly ||
      quickViewErrorDetails.length > 0
  );

  export const selectColumnCreationFlow = createSelector(
    kanbanBoard,
    (state: KanbanBoardState): ColumnCreationFlow => state.columnCreationFlow
  );

  export const selectInstallationOfTemporaryColumn = createSelector(
    kanbanBoard,
    (state: KanbanBoardState): DashboardInstallationData =>
      state.columns.find((column) => column.isTemporary).installations[0]
  );

  export const selectIdOfOriginalColumnDuringColumnCreationAndMoveProcess = createSelector(
    kanbanBoard,
    (state: KanbanBoardState): number => state.idOfOriginalColumnDuringColumnCreationAndMoveProcess
  );

  export const selectEditColumnPayloadById = (
    id: number
  ): MemoizedSelector<
    AppState,
    DashboardEditColumnPayload,
    DefaultProjectorFn<DashboardEditColumnPayload>
  > => {
    return createSelector(
      kanbanBoard,
      (state: KanbanBoardState): DashboardEditColumnPayload => {
        const column = state.columns.find((column) => column.id === id);

        return {
          leftNeighbor: column.leftNeighbor,
          version: column.version,
          name: column.name,
          sortOrder: column.sortOrder,
          ...(column.selfDefined && { installations: column.installationIds }),
        };
      }
    );
  };

  export const selectIsSpecificColumnSelfDefined = (
    id: number
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> => {
    return createSelector(
      kanbanBoard,
      (state: KanbanBoardState): boolean =>
        state.columns.find((column) => column.id === id).selfDefined
    );
  };

  export const selectPayloadDataBeforeMovingInstallation = (
    firstColumnId: number,
    secondColumnId: number
  ): MemoizedSelector<
    AppState,
    [DashboardColumnDataWithoutInstallationDetails, DashboardColumnDataWithoutInstallationDetails],
    DefaultProjectorFn<
      [DashboardColumnDataWithoutInstallationDetails, DashboardColumnDataWithoutInstallationDetails]
    >
  > => {
    return createSelector(kanbanBoard, (state: KanbanBoardState): [
      DashboardColumnDataWithoutInstallationDetails,
      DashboardColumnDataWithoutInstallationDetails
    ] => {
      const firstColumn = state.columns.find((column) => column.id === firstColumnId);
      const secondColumn = state.columns.find((column) => column.id === secondColumnId);

      const output: [
        DashboardColumnDataWithoutInstallationDetails,
        DashboardColumnDataWithoutInstallationDetails
      ] = [
        getDataFromColumnBeforeMovingInstallation(firstColumn),
        getDataFromColumnBeforeMovingInstallation(secondColumn),
      ];

      return output;
    });
  };

  export const selectInstallationListState = createSelector(
    kanbanBoard,
    (state: KanbanBoardState): InstallationListDashboardState => state.installationList
  );

  export const selectButtonFilterData = createSelector(
    kanbanBoard,
    (state: KanbanBoardState): DashboardListFilterButtonData[] | null =>
      state.installationList.buttonFilterData
  );

  export const selectNumberOfAllFetchableInstallations = createSelector(
    kanbanBoard,
    (state: KanbanBoardState): number | undefined =>
      state.installationList.buttonFilterData?.find(
        (button) => button.customName === ButtonFilterDataName.All
      )?.quantity
  );

  export function isFilterButtonActive(
    filterName: DashboardFilterName,
    filterValue: DashboardFilterValues[] | boolean
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(kanbanBoard, (state: KanbanBoardState): boolean => {
      if (Array.isArray(filterValue)) {
        return filterValue.every((filterValue) =>
          state.selectedFilters.some(
            (selectedFilter) =>
              selectedFilter.filterName === filterName && selectedFilter.value === filterValue
          )
        );
      } else {
        return state.selectedFilters.some((selectedFilter) => selectedFilter.filterName);
      }
    });
  }

  export const selectIsLimitOfSelfDefinedColumnsHit = createSelector(
    kanbanBoard,
    (state: KanbanBoardState): boolean =>
      state.columns.filter((column) => column.selfDefined).length >= limitOfSelfDefinedColumns
  );

  function getDataFromColumnBeforeMovingInstallation(
    column: DashboardInstallationColumn
  ): DashboardColumnDataWithoutInstallationDetails {
    return {
      leftNeighbor: column.leftNeighbor,
      version: column.version,
      name: column.name,
      sortOrder: column.sortOrder,
      installations: column.installationIds,
      id: column.id,
      selfDefined: column.selfDefined,
    };
  }

  export const selectInstallationListCursor = createSelector(
    kanbanBoard,
    (state: KanbanBoardState): string => state.installationList.cursor
  );
}
